import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { PROFIL_STATUS, PIPELINE_EVENT_TYPEN } from '../../lib/constants'
import './AdminPages.css'

// Pipeline columns based on PROFIL_STATUS
const COLUMNS = [
  { status: 'neu', label: 'Neu', icon: '📥', color: '#3B82F6' },
  { status: 'geprueft', label: 'Geprüft', icon: '🔍', color: '#8B5CF6' },
  { status: 'in_vermittlung', label: 'In Vermittlung', icon: '🎯', color: '#F59E0B' },
  { status: 'vorgestellt', label: 'Vorgestellt', icon: '📤', color: '#EC4899' },
  { status: 'interview', label: 'Interview', icon: '💼', color: '#6366F1' },
  { status: 'platziert', label: 'Platziert', icon: '🎉', color: '#059669' },
  { status: 'abgesagt', label: 'Abgesagt', icon: '❌', color: '#EF4444' },
]

interface PipelineCard {
  id: string
  vorname: string
  nachname: string
  qualifikation: string
  bundesland: string
  status: string
  created_at: string
  telefon: string | null
  email: string
  matches_count?: number
  top_match_score?: number
  latest_event?: string
  latest_event_date?: string
}

export default function PipelineBoard() {
  const navigate = useNavigate()
  const [cards, setCards] = useState<PipelineCard[]>([])
  const [loading, setLoading] = useState(true)
  const [draggedId, setDraggedId] = useState<string | null>(null)
  const [dropTarget, setDropTarget] = useState<string | null>(null)
  const [showEvent, setShowEvent] = useState<string | null>(null)
  const [eventForm, setEventForm] = useState({ event_typ: '', notizen: '', naechster_schritt: '' })
  const [saving, setSaving] = useState(false)

  const loadCards = useCallback(async () => {
    setLoading(true)
    // Load Pflegekräfte with match counts
    const { data: pfk } = await supabase
      .from('pflegekraefte')
      .select('id, vorname, nachname, qualifikation, bundesland, status, created_at, telefon, email')
      .order('created_at', { ascending: false })

    if (pfk) {
      // Get match counts per pflegekraft
      const { data: matchData } = await supabase
        .from('matches')
        .select('pflegekraft_id, score, kategorie')
        .in('pflegekraft_id', pfk.map(p => p.id))
        .order('score', { ascending: false })

      // Get latest pipeline events
      const { data: events } = await supabase
        .from('pipeline_events')
        .select('pflegekraft_id, event_typ, created_at')
        .in('pflegekraft_id', pfk.map(p => p.id))
        .order('created_at', { ascending: false })

      const enriched: PipelineCard[] = pfk.map(p => {
        const pMatches = matchData?.filter(m => m.pflegekraft_id === p.id) || []
        const topScore = pMatches.length > 0 ? Math.max(...pMatches.map(m => m.score)) : undefined
        const latestEvt = events?.find(e => e.pflegekraft_id === p.id)
        return {
          ...p,
          matches_count: pMatches.length,
          top_match_score: topScore,
          latest_event: latestEvt?.event_typ,
          latest_event_date: latestEvt?.created_at,
        }
      })
      setCards(enriched)
    }
    setLoading(false)
  }, [])

  useEffect(() => { loadCards() }, [loadCards])

  async function moveCard(cardId: string, newStatus: string) {
    await supabase.from('pflegekraefte').update({ status: newStatus }).eq('id', cardId)
    setCards(prev => prev.map(c => c.id === cardId ? { ...c, status: newStatus } : c))
  }

  function handleDragStart(id: string) { setDraggedId(id) }
  function handleDragOver(e: React.DragEvent, status: string) { e.preventDefault(); setDropTarget(status) }
  function handleDragLeave() { setDropTarget(null) }
  function handleDrop(status: string) {
    if (draggedId) moveCard(draggedId, status)
    setDraggedId(null)
    setDropTarget(null)
  }

  async function addEvent(cardId: string) {
    if (!eventForm.event_typ) return
    setSaving(true)
    await supabase.from('pipeline_events').insert({
      pflegekraft_id: cardId,
      event_typ: eventForm.event_typ,
      notizen: eventForm.notizen || null,
      naechster_schritt: eventForm.naechster_schritt || null,
    })
    setEventForm({ event_typ: '', notizen: '', naechster_schritt: '' })
    setShowEvent(null)
    setSaving(false)
    await loadCards()
  }

  const getQualLabel = (val: string) => {
    const map: Record<string, string> = {
      pflegefachkraft: 'PFK', pflegehilfskraft: 'PHK', mfa: 'MFA',
      altenpfleger: 'AP', guk: 'GuK', sonstiges: 'Sonst.'
    }
    return map[val] || val
  }

  if (loading) return <div className="table-loading"><div className="loading-spinner" /></div>

  return (
    <div className="admin-page animate-fade-in-up pipeline-page">
      <div className="page-header">
        <div>
          <h1>📈 Pipeline Board</h1>
          <p className="page-subtitle">
            {cards.length} Kandidat{cards.length !== 1 ? 'en' : ''} im Prozess · 
            Drag & Drop zum Verschieben
          </p>
        </div>
      </div>

      <div className="kanban-board">
        {COLUMNS.map(col => {
          const colCards = cards.filter(c => c.status === col.status)
          const isTarget = dropTarget === col.status
          return (
            <div
              key={col.status}
              className={`kanban-column ${isTarget ? 'kanban-column--drop-target' : ''}`}
              onDragOver={(e) => handleDragOver(e, col.status)}
              onDragLeave={handleDragLeave}
              onDrop={() => handleDrop(col.status)}
            >
              <div className="kanban-column-header" style={{ '--col-color': col.color } as React.CSSProperties}>
                <span className="kanban-col-icon">{col.icon}</span>
                <span className="kanban-col-title">{col.label}</span>
                <span className="kanban-col-count">{colCards.length}</span>
              </div>

              <div className="kanban-cards">
                {colCards.map(card => (
                  <div
                    key={card.id}
                    className={`kanban-card ${draggedId === card.id ? 'kanban-card--dragging' : ''}`}
                    draggable
                    onDragStart={() => handleDragStart(card.id)}
                  >
                    <div className="kanban-card-top">
                      <span
                        className="kanban-card-name"
                        onClick={() => navigate(`/admin/pflegekraefte/${card.id}`)}
                      >
                        {card.vorname} {card.nachname}
                      </span>
                      <span className="kanban-card-qual">{getQualLabel(card.qualifikation)}</span>
                    </div>

                    {card.bundesland && (
                      <div className="kanban-card-meta">📍 {card.bundesland}</div>
                    )}

                    {(card.matches_count !== undefined && card.matches_count > 0) && (
                      <div className="kanban-card-matches">
                        <span className="kanban-matches-count">🎯 {card.matches_count} Matches</span>
                        {card.top_match_score && (
                          <span className="kanban-top-score" style={{ 
                            color: card.top_match_score >= 80 ? '#10B981' : card.top_match_score >= 60 ? '#3B82F6' : '#F59E0B'
                          }}>
                            Best: {card.top_match_score}%
                          </span>
                        )}
                      </div>
                    )}

                    {card.latest_event && (
                      <div className="kanban-card-event">
                        {PIPELINE_EVENT_TYPEN.find(e => e.value === card.latest_event)?.icon}{' '}
                        {PIPELINE_EVENT_TYPEN.find(e => e.value === card.latest_event)?.label}
                      </div>
                    )}

                    <div className="kanban-card-actions">
                      <button
                        className="kanban-action-btn"
                        onClick={(e) => { e.stopPropagation(); setShowEvent(showEvent === card.id ? null : card.id) }}
                        title="Event hinzufügen"
                      >
                        + Event
                      </button>
                    </div>

                    {/* Event Form */}
                    {showEvent === card.id && (
                      <div className="kanban-event-form animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
                        <select
                          value={eventForm.event_typ}
                          onChange={(e) => setEventForm(p => ({ ...p, event_typ: e.target.value }))}
                          className="kanban-event-select"
                        >
                          <option value="">Event-Typ wählen...</option>
                          {PIPELINE_EVENT_TYPEN.map(e => (
                            <option key={e.value} value={e.value}>{e.icon} {e.label}</option>
                          ))}
                        </select>
                        <textarea
                          value={eventForm.notizen}
                          onChange={(e) => setEventForm(p => ({ ...p, notizen: e.target.value }))}
                          placeholder="Notizen..."
                          rows={2}
                          className="kanban-event-textarea"
                        />
                        <button
                          className="btn btn--primary kanban-event-save"
                          onClick={() => addEvent(card.id)}
                          disabled={!eventForm.event_typ || saving}
                        >
                          {saving ? 'Speichern...' : 'Speichern'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}

                {colCards.length === 0 && (
                  <div className="kanban-empty">
                    <span>Keine Kandidaten</span>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
